import { DEFAULT_PRICING_RULES, ruleValue } from '@repo/db/pricing-rules'

/**
 * How much raw footage one montage order may bring (spec §8 "Limit surowego
 * materiału" · G7).
 *
 * The limit exists because editing time scales with what has to be watched, not
 * with what comes out: a 20-minute film cut from 40 minutes of tape and one cut
 * from four hours are the same delivery and completely different jobs. Spec §8
 * prices the difference rather than refusing it — 2 zł net per minute over,
 * added to the order UP FRONT, so nobody starts work on an order that has
 * already gone wrong commercially.
 *
 * Two shapes of limit, one per product, and the same ratio idea behind both:
 *
 *   FILM   the band's ceiling × 2.5 — "20 do 30 minut to limit 75 minut
 *          surówki", identical on every level (spec §8).
 *   REEL   10 minutes per reel — "60 minut na rolki to albo 6 rolek, albo
 *          dopłata".
 *
 * Pure integer arithmetic. Minutes are whole minutes; the ×2.5 is the one place
 * a fraction appears and it is floored, because half a minute of allowance is
 * not an allowance.
 */

/**
 * The ratio a montage category allows, when it has not said otherwise.
 *
 * Spec §8: „20 do 30 minut to limit 75 minut surówki" — the band's ceiling
 * times this, identical on every level.
 */
export const FOOTAGE_RATIO = 2.5

/**
 * Categories that allow a DIFFERENT amount per level, and what each one allows.
 *
 * Owner, 14.10: *„inne przeliczniki są do filmu i lifestyle, a inne do
 * podcastu"*. One ratio for the whole of montage was a fair approximation while
 * the categories were copies of each other; they are not. A podcast arrives at
 * roughly the length it goes out — two people talking for an hour produce an
 * hour of tape — while a lifestyle film is cut from several times its own
 * length, and holding both to 2.5 either gives the podcast an allowance nobody
 * needs or squeezes the film into one nobody can work in.
 *
 * Keyed by the offering's slug and then by the level's, because the step from
 * Clean to Advanced is itself what the customer buys here: 1,3× leaves room for
 * one bad take, 2× leaves room for a conversation that ran long and a second
 * attempt at the intro.
 *
 * In CODE and not in a column, like `FOOTAGE_RATIO` beside it, so the number a
 * basket quotes and the number a checkout charges cannot drift apart between
 * two deploys. The day somebody wants to turn it in the panel it becomes a
 * column, and this map becomes its default.
 */
const FOOTAGE_RATIOS: Readonly<Record<string, Readonly<Record<string, number>>>> = {
  'montaz-podcast': { starter: 1.3, creator: 1.5, pro: 2 },
}

/** What this level of this offering multiplies its length band by. */
export function footageRatioFor(offeringSlug: string, variantSlug: string): number {
  return FOOTAGE_RATIOS[offeringSlug]?.[variantSlug] ?? FOOTAGE_RATIO
}

/** Spec §8: "Limit rolki: 10 minut surówki na 1 rolkę." */
export const FOOTAGE_MINUTES_PER_REEL = 10

/**
 * Spec §8: "2 zł netto za każdą minutę ponad limit." In grosze.
 *
 * The default for `footage.overage_net_minor_per_minute`, which Cennik owns.
 * The two LIMITS above it stay constants: how much tape a film may be cut from
 * is a statement about editing work, not a price, and moving it would rewrite
 * what every level promises rather than what it costs.
 */
export const FOOTAGE_OVERAGE_NET_MINOR = ruleValue(
  DEFAULT_PRICING_RULES,
  'footage.overage_net_minor_per_minute',
)

/**
 * How much tape a film in this band may be cut from.
 *
 * FLOORED. `2.5 × 25` is 62.5, and half a minute of allowance is not an
 * allowance — it is a number that makes the limit unstatable on a screen and
 * turns "you are one minute over" into an argument about rounding.
 */
export function filmFootageLimit(bandMaxMinutes: number, ratio: number = FOOTAGE_RATIO): number {
  return Math.floor(bandMaxMinutes * ratio)
}

/** How much tape `reels` reels may be cut from. */
export function reelFootageLimit(reels: number): number {
  return Math.max(0, Math.trunc(reels)) * FOOTAGE_MINUTES_PER_REEL
}

/** Minutes over the allowance. Never negative — bringing less is not a credit. */
export function footageOverageMinutes(limitMinutes: number, declaredMinutes: number): number {
  return Math.max(0, Math.ceil(declaredMinutes) - limitMinutes)
}

/** What those minutes cost, NET grosze. One rate, the same on every level. */
export function footageOverageNetMinor(
  limitMinutes: number,
  declaredMinutes: number,
  perMinuteNetMinor: number = FOOTAGE_OVERAGE_NET_MINOR,
): number {
  return footageOverageMinutes(limitMinutes, declaredMinutes) * perMinuteNetMinor
}

/**
 * The limit for one basket line, whichever product it is.
 *
 * `null` when the line has no footage rule at all — a thumbnail, a hire, a
 * studio hour, or a reel cut from a film in the same order. Callers must treat
 * `null` as "do not ask about footage" rather than as a limit of zero, which
 * would bill every thumbnail in the basket.
 */
export function footageLimitFor(line: {
  pricingUnit: string
  /** The film's band ceiling, when it has one. */
  lengthBandMax?: number | null
  /** How many reels this line is. */
  quantity: number
  /** Whether the offering asks for source material at all (`offering.source_mode`). */
  needsSource: boolean
  /**
   * Is this line cut from a film in the same order (spec §8 · G8)?
   *
   * Then it has no allowance of its own and no fee: the tape was declared once,
   * on the film, and charged once. Answered HERE rather than by each caller
   * remembering to skip the question, because "one derivation" is the whole
   * reason this function exists — the browser draws the field from it and the
   * checkout prices from it, and a fee shown in one and not the other is the
   * bug it was written to prevent.
   */
  fromFilm?: boolean
  /**
   * Which offering and which level, so the ratio can differ between them.
   *
   * Optional, and the fallback is the flat 2.5 every caller got before. A
   * caller that cannot say which level it is asking about gets the old answer
   * rather than a wrong one.
   */
  offeringSlug?: string
  variantSlug?: string
}): number | null {
  if (line.fromFilm) return null
  if (!line.needsSource) return null
  if (line.pricingUnit === 'MINUTE') {
    return line.lengthBandMax === null || line.lengthBandMax === undefined
      ? null
      : filmFootageLimit(
          line.lengthBandMax,
          line.offeringSlug !== undefined && line.variantSlug !== undefined
            ? footageRatioFor(line.offeringSlug, line.variantSlug)
            : FOOTAGE_RATIO,
        )
  }
  return reelFootageLimit(line.quantity)
}
